type PiiPatternKey =
  | 'creditCardNumber'
  | 'streetAddress'
  | 'phoneNumber'
  | 'usSocialSecurityNumber'
  | 'emailAddress'
  | 'password'
  | 'credentials';

const APT_UNIT_PATTERN =
  /(apt|bldg|dept|fl|hngr|lot|pier|rm|ste|slip|trlr|unit|#)\.? *[a-z0-9-]+\b/i;
const PO_BOX_PATTERN = /P\.? ?O\.? *Box +\d+/i;
const ROAD_PATTERN =
  /(?:street|st|road|rd|avenue|ave|drive|dr|loop|court|ct|circle|cir|lane|ln|boulevard|blvd|way)\.?\b/i;

export const piiPatterns: Record<PiiPatternKey, RegExp> = {
  creditCardNumber:
    /\d{4}[ -]?\d{4}[ -]?\d{4}[ -]?\d{4}|\d{4}[ -]?\d{6}[ -]?\d{4}\d?/g,
  streetAddress: new RegExp(
    `(\\d+\\s*(\\w+ ){1,2}${ROAD_PATTERN.source}(\\s+${APT_UNIT_PATTERN.source})?)|(${PO_BOX_PATTERN.source})`,
    'gi',
  ),
  phoneNumber:
    /(?:\+1\s)?(?:\([0-9]{3}\)\s+[0-9]{3}-[0-9]{4}|[0-9]{3}-[0-9]{3}-[0-9]{4})\b|\b[2-9]\d{2}[2-9]\d{6}\b/g,
  usSocialSecurityNumber: /\b\d{3}[ -.]\d{2}[ -.]\d{4}\b/g,
  emailAddress: /([a-z0-9_\-.+]+)@\w+(\.\w+)*/gi,
  password: /(pass(word|phrase)?|secret): \S+/gi,
  credentials:
    /(login( cred(ential)?s| info(rmation)?)?|cred(ential)?s) ?:\s*\S+\s+\/?\s*\S+/gi,
};

/** Fast guards — skip full regex when a pattern cannot match. */
export const patternPrechecks: Record<
  PiiPatternKey,
  (value: string) => boolean
> = {
  creditCardNumber: (value) =>
    /\d{4}[ -]?\d{4}[ -]?\d{4}[ -]?\d{4}/.test(value) ||
    /\d{4}[ -]?\d{6}[ -]?\d{4}\d?/.test(value),
  streetAddress: (value) =>
    PO_BOX_PATTERN.test(value) ||
    (/\d+\s+\S/.test(value) && ROAD_PATTERN.test(value)),
  phoneNumber: (value) =>
    /(?:\+1\s)?(?:\([0-9]{3}\)\s+[0-9]{3}-[0-9]{4}|[0-9]{3}-[0-9]{3}-[0-9]{4})\b/.test(
      value,
    ) || /\b[2-9]\d{2}[2-9]\d{6}\b/.test(value),
  usSocialSecurityNumber: (value) => /\d{3}[ \-.]\d{2}[ \-.]\d{4}/.test(value),
  emailAddress: (value) => value.includes('@'),
  password: (value) => /(?:pass(?:word|phrase)?|secret): \S+/i.test(value),
  credentials: (value) =>
    /(?:login(?: cred(?:ential)?s| info(?:rmation)?)?|cred(?:ential)?s)\s*:\s*\S+\s+\/?\s*\S+/i.test(
      value,
    ),
};

export type { PiiPatternKey };

export const MESSAGE_PATTERN_KEYS = Object.keys(piiPatterns) as PiiPatternKey[];

/** Stack traces skip expensive patterns that rarely appear in frames. */
export const STACK_PATTERN_KEYS: readonly PiiPatternKey[] = [
  'emailAddress',
  'password',
  'credentials',
  'usSocialSecurityNumber',
  'phoneNumber',
];
